import React from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import {
  ClaimSummaryDTO,
  CreateInsuranceWorkflowResolutionDTO,
  InsuranceWorkflowResolutionTypeOptionDTO,
  PatientDTO,
} from 'dtos';
import {
  InsuranceWorkflowResolutionType,
} from 'dtos/insuranceWorkflowResolutionType';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Controller from 'core/components/FormController';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import CurrencyTextField from 'core/components/CurrencyTextField';
import TextArea from 'pages/Dashboard/components/Input/Textarea';
import {
  useNotify,
} from 'core/hooks';
import {
  billingEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useMarkInsuranceWorkflowResolved,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  notEmptyString,
} from '@populateui/utils';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import {
  formatDateTime,
} from 'utils/date';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import head from 'lodash/head';
import {
  validateAmount,
} from 'utils/misc';
import {
  useInsuranceMetaData,
} from 'views/EMR/components/Insurance/hooks';
import Select from 'core/components/Select';

export type InsuranceResolutionDialogParams = {
  claims: Partial<ClaimSummaryDTO>[];
  insuranceBalance: number;
  encounterId?: number;
  visitDateTime?: string;
  patient?: Partial<PatientDTO>;
};

type InsuranceResolutionForm = {
  claim?: Partial<ClaimSummaryDTO>;
  amount?: string;
  resolutionType?: InsuranceWorkflowResolutionTypeOptionDTO;
  note?: string;
};

type Props = InsuranceResolutionDialogParams & {
  onClose: () => void;
};

type TitleProps = Pick<InsuranceResolutionDialogParams, 'visitDateTime' | 'patient'>;

function Title({ visitDateTime, patient }: TitleProps) {
  return (
    <div className="flex flex-col gap-1 mb-2">
      <div className="text-lg font-bold">Resolve Insurance Balance</div>
      <div className="text-xs text-gray-500 flex items-center gap-1 font-medium">
        <span>
          {getDisplayName(patient?.formattedNames)}
        </span>
        <span className="font-bold">·</span>
        <span>Visited on</span>
        <span>
          {formatDateTime(visitDateTime ?? '')}
        </span>
      </div>
    </div>
  );
}

export default function InsuranceResolutionDialog({
  claims,
  insuranceBalance,
  encounterId,
  visitDateTime,
  patient,
  onClose,
}: Props) {
  const notify = useNotify();
  const {
    mutateAsync: markInsuranceWorkflowResolved,
    isPending: isMarkingInsuranceWorkflowResolved,
  } = useMarkInsuranceWorkflowResolved();

  const formProps = useForm<InsuranceResolutionForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      claim: head(claims),
      amount: insuranceBalance.toString(),
      note: '',
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = formProps;

  const onSubmit = async ({
    note,
    resolutionType,
  }: InsuranceResolutionForm) => {
    try {
      const payload: CreateInsuranceWorkflowResolutionDTO = {
        encounterId,
        resolutionType: resolutionType?.value as InsuranceWorkflowResolutionType,
        note: notEmptyString(note)
          ? note?.trim()
          : null,
      };

      await markInsuranceWorkflowResolved(payload);
      billingEventChannel.emit('onChargesInfoChange', {
        encounterId: encounterId ?? 0,
      });
      notify({
        message: 'Insurance workflow resolution saved successfully.',
        variant: 'success',
      });
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const {
    insuranceWorkflowResolutionTypeOptions,
  } = useInsuranceMetaData();

  return (
    <Dialog
      open
      title={(<Title visitDateTime={visitDateTime} patient={patient} />)}
      onClose={onClose}
      onConfirm={onClose}
      showActions={false}
      dialogClassName="rounded-lg"
      className="!w-[32rem] !space-y-0"
      titleClassName="mb-4"
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-4 populate-form mt-4">
          <div className="flex flex-col gap-form-row">
            <Controller
              control={control}
              name="amount"
              rules={{
                validate: (value) => validateAmount(value ?? '') || 'Enter a valid amount',
              }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="resolution-amount"
                  label="Insurance Balance"
                  required
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full"
                >
                  <CurrencyTextField
                    {...field}
                    defaultValue=""
                    disabled
                  />
                </Wrapper>
              )}
            />
            <Controller
              control={control}
              name="resolutionType"
              rules={{ required: 'Select resolution type' }}
              render={({ field, fieldState: { error } }) => (
                <Wrapper
                  id="resolution-type"
                  label="Closure Reason"
                  required
                  error={!isNil(error)}
                  showErrorText={false}
                  className="w-full"
                >
                  <Select<InsuranceWorkflowResolutionTypeOptionDTO>
                    {...field}
                    value={field.value ?? null}
                    className="w-full"
                    error={!isNil(error)}
                    showErrorText={false}
                    placeholder="Select"
                    options={insuranceWorkflowResolutionTypeOptions}
                    getValue={(option) => option?.value ?? ''}
                    getLabel={(option) => option?.displayName ?? ''}
                    onChange={(value) => {
                      field.onChange(value as InsuranceWorkflowResolutionTypeOptionDTO);
                    }}
                  />
                </Wrapper>
              )}
            />
            <Controller
              control={control}
              name="note"
              render={({ field }) => (
                <TextArea
                  {...field}
                  id="resolution-note"
                  label="Note"
                  placeholder="Add a note"
                  rows={4}
                  className="w-full"
                />
              )}
            />
          </div>

          <div className="flex justify-between gap-3 pt-2">
            <Button
              variant="outlined"
              type="button"
              onClick={onClose}
              className="min-w-24"
            >
              Close
            </Button>
            <DisabledFormTooltip>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitting || !isValid || isMarkingInsuranceWorkflowResolved}
                className={clsx('min-w-24', {
                  '!cursor-wait': isSubmitting || isMarkingInsuranceWorkflowResolved,
                })}
                onClick={handleSubmit(onSubmit)}
              >
                Confirm
              </Button>
            </DisabledFormTooltip>
          </div>
        </div>
      </FormProvider>
    </Dialog>
  );
}
